import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Outcome, OutcomeOperator, AndOrOperator } from '../../../widgets.model';

export interface OutcomeGeneratorData {
  minScore: number;
  maxScore: number;
  inputComponents: {
    name: string;
    displayName: string;
    values: string[];
    valuesWithScore: { value: string; score: number; isCorrect?: boolean }[];
    isMultiSelect: boolean;
  }[];
}

@Component({
  selector: 'app-outcome-generator',
  templateUrl: './outcome-generator.component.html',
  styleUrls: ['./outcome-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OutcomeGeneratorComponent {
  mode: 'score' | 'answer' = 'score';
  numberOfOutcomes: number = 1;
  maxOutcomes: number = 10;

  constructor(
    public dialogRef: MatDialogRef<OutcomeGeneratorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OutcomeGeneratorData,
  ) {
    this.updateMaxOutcomes();
  }

  updateMaxOutcomes() {
    if (this.mode === 'score') {
      const range = this.data.maxScore - this.data.minScore + 1;
      this.maxOutcomes = Math.max(1, range);
    } else {
      // For answer based, we can technically have as many outcomes as we want,
      // but practically it makes sense to limit it somewhat or base it on number of questions/answers.
      // The prompt says "1-n and distribute".
      // Let's set a reasonable limit or dynamic based on total answers?
      // For now, let's just allow up to 20 or so.
      this.maxOutcomes = 20;
    }
    if (this.numberOfOutcomes > this.maxOutcomes) {
      this.numberOfOutcomes = this.maxOutcomes;
    }
  }

  onModeChange() {
    this.numberOfOutcomes = 1;
    this.updateMaxOutcomes();
  }

  generate() {
    let outcomes: Outcome[] = [];
    if (this.mode === 'score') {
      outcomes = this.generateScoreBasedOutcomes();
    } else {
      outcomes = this.generateAnswerBasedOutcomes();
    }
    this.dialogRef.close(outcomes);
  }

  private generateScoreBasedOutcomes(): Outcome[] {
    const outcomes: Outcome[] = [];
    const min = this.data.minScore;
    const max = this.data.maxScore;
    const count = this.numberOfOutcomes;
    const range = max - min + 1;

    if (range <= 0) return [];

    const step = Math.floor(range / count);
    const remainder = range % count;

    let currentMin = min;

    for (let i = 0; i < count; i++) {
      // Distribute remainder to the first 'remainder' items
      const currentStep = step + (i < remainder ? 1 : 0);
      const start = currentMin;
      const end = currentMin + currentStep - 1;
      currentMin = end + 1;

      const outcome: Outcome = {
        stateId: null, // Will be assigned by the caller
        isDefault: false,
        answers: [],
        customDataUpdate: undefined,
      };

      if (start === end) {
        outcome.answers.push({
          question: 'score',
          operator: OutcomeOperator.Equals,
          value: start.toString(),
          andOrOperator: AndOrOperator.And,
        });
      } else {
        // Range: >= start AND <= end
        outcome.answers.push({
          question: 'score',
          operator: OutcomeOperator.GreaterThanOrEqual,
          value: start.toString(),
          andOrOperator: AndOrOperator.And,
        });
        outcome.answers.push({
          question: 'score',
          operator: OutcomeOperator.LessThanOrEqual,
          value: end.toString(),
          andOrOperator: AndOrOperator.And,
        });
      }
      outcomes.push(outcome);
    }

    return outcomes;
  }

  private generateAnswerBasedOutcomes(): Outcome[] {
    const outcomes: Outcome[] = [];
    const count = this.numberOfOutcomes;

    // Filter out 'score' from inputComponents
    const questions = this.data.inputComponents.filter((c) => c.name !== 'score');

    if (questions.length === 0) return [];

    // Initialize outcomes
    for (let i = 0; i < count; i++) {
      outcomes.push({
        stateId: null,
        isDefault: false,
        answers: [],
        customDataUpdate: undefined,
      });
    }

    if (count === 1) {
      // 1 outcome: Add all questions to this outcome
      const outcome = outcomes[0];
      questions.forEach((q) => {
        const val = this.getBestValueForQuestion(q);
        if (val) {
          outcome.answers.push({
            question: q.name,
            operator: OutcomeOperator.Equals,
            value: val,
            andOrOperator: AndOrOperator.And,
          });
        }
      });
    } else {
      // Distribute questions evenly across outcomes
      // "distribute so instead of score it has taken one answer from each question and inputted evenly into each outcome"
      // Example: 3 questions, 2 outcomes.
      // Outcome 1: Q1, Q3
      // Outcome 2: Q2

      questions.forEach((q, index) => {
        const outcomeIndex = index % count;
        const outcome = outcomes[outcomeIndex];
        const val = this.getBestValueForQuestion(q);

        if (val) {
          outcome.answers.push({
            question: q.name,
            operator: OutcomeOperator.Equals,
            value: val,
            andOrOperator: AndOrOperator.And,
          });
        }
      });
    }

    return outcomes;
  }

  private getBestValueForQuestion(question: any): string | null {
    if (!question.values || question.values.length === 0) return null;

    // "If any of them are correct answer, use that one"
    const correctValue = question.valuesWithScore?.find((v) => v.isCorrect);
    if (correctValue) {
      return correctValue.value;
    }

    // "otherwise use first"
    return question.values[0];
  }

  cancel() {
    this.dialogRef.close();
  }
}
